import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import PageHeader from "../../components/PageHeader";
import { LABELS } from "../../platform/config";
import { useUser } from "../../contexts/UserContext";
import {
  ConsentForm,
  RecordOptions,
  createConsentForm,
  getRecordOptions,
  listConsentForms,
  saveRecordOptions,
  updateConsentForm,
} from "../../services/clientRecordsService";

/**
 * One editable suggestion list (allergies or conditions). Type and press
 * Enter to add; the x on a chip removes it.
 */
const OptionList: React.FC<{
  label: string;
  hint: string;
  value: string[];
  disabled: boolean;
  onChange: (value: string[]) => void;
}> = ({ label, hint, value, disabled, onChange }) => (
  <Box>
    <Autocomplete
      multiple
      freeSolo
      options={[]}
      value={value}
      disabled={disabled}
      onChange={(_, next) => onChange((next as string[]).map((v) => v.trim()).filter(Boolean))}
      renderTags={(tags, getTagProps) =>
        tags.map((tag, index) => {
          const { key, ...rest } = getTagProps({ index });
          return <Chip key={key} {...rest} size="small" label={tag} />;
        })
      }
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder="Type and press Enter" size="small" />
      )}
    />
    <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", mt: 0.5 }}>{hint}</Typography>
  </Box>
);

type Draft = { id?: string; title: string; body: string; requiresSignature: boolean; isActive: boolean };

const blank: Draft = { title: "", body: "", requiresSignature: true, isActive: true };

/**
 * The consent texts clients sign (treatment consent, data privacy, photo
 * consent, a sports waiver...). Signed copies keep the text as it was, so
 * editing a form never changes what someone already signed.
 */
const ConsentForms: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useUser();
  const isOwner = user?.role !== "EMPLOYEE";
  const [forms, setForms] = useState<ConsentForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState<RecordOptions | null>(null);
  const [optionsDirty, setOptionsDirty] = useState(false);
  const [savingOptions, setSavingOptions] = useState(false);

  useEffect(() => {
    getRecordOptions(true)
      .then(setOptions)
      .catch(() => setOptions({ allergies: [], conditions: [], fileTypes: [] }));
  }, []);

  const saveOptions = async () => {
    if (!options) return;
    setSavingOptions(true);
    try {
      setOptions(await saveRecordOptions(options));
      setOptionsDirty(false);
      toast.success("Lists saved.", { position: "top-right" });
    } catch (err: any) {
      const message = err?.response?.data?.message;
      toast.error((Array.isArray(message) ? message[0] : message) || "Could not save.", { position: "top-right" });
    }
    setSavingOptions(false);
  };

  const load = useCallback(async () => {
    try {
      setForms(await listConsentForms(true));
    } catch {
      // The interceptor already showed the error.
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const payload = {
        title: draft.title.trim(),
        body: draft.body.trim(),
        requiresSignature: draft.requiresSignature,
        isActive: draft.isActive,
      };
      if (draft.id) await updateConsentForm(draft.id, payload);
      else await createConsentForm(payload);
      toast.success("Consent form saved.", { position: "top-right" });
      setDraft(null);
      await load();
    } catch (err: any) {
      const message = err?.response?.data?.message;
      toast.error((Array.isArray(message) ? message[0] : message) || "Could not save.", { position: "top-right" });
    }
    setSaving(false);
  };

  return (
    <Box sx={{ width: "100%", mt: { xs: 2, sm: 3 } }}>
      <PageHeader
        title={`${LABELS.clientFile} settings`}
        subtitle={`Consent forms ${LABELS.clients.toLowerCase()} sign, the allergy and condition lists offered in their ${LABELS.medicalInfo.toLowerCase()}, and file types.`}
        actions={
          isOwner && (
            <Button
              variant="contained"
              disableElevation
              startIcon={<AddIcon />}
              onClick={() => setDraft({ ...blank })}
              data-tour="new-consent-form"
              sx={{ textTransform: "none" }}
            >
              New form
            </Button>
          )
        }
      />

      {!isOwner && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Only the account owner can change these settings.
        </Alert>
      )}

      <Typography component="h2" sx={{ fontWeight: 700, fontSize: "1.1rem", mb: 1.5 }}>
        Consent forms
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : forms.length === 0 ? (
        <Paper elevation={0} sx={{ border: "1px dashed", borderColor: "divider", borderRadius: 3, p: 3, textAlign: "center" }}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>No consent forms yet</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: "0.9rem" }}>
            For example a treatment consent, a data privacy notice or a photo consent.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: "grid", gap: 1.5 }}>
          {forms.map((form) => (
            <Paper
              key={form.id}
              elevation={0}
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, p: 2, opacity: form.isActive ? 1 : 0.6 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={{ fontWeight: 700, flexGrow: 1 }}>{form.title}</Typography>
                {form.requiresSignature && <Chip size="small" variant="outlined" label="Signature" />}
                {!form.isActive && <Chip size="small" label="Off" />}
                {isOwner && (
                  <Button
                    size="small"
                    onClick={() =>
                      setDraft({
                        id: form.id,
                        title: form.title,
                        body: form.body,
                        requiresSignature: form.requiresSignature,
                        isActive: form.isActive,
                      })
                    }
                    sx={{ textTransform: "none" }}
                  >
                    Edit
                  </Button>
                )}
              </Box>
              <Typography
                sx={{
                  mt: 0.75,
                  fontSize: "0.88rem",
                  color: "text.secondary",
                  whiteSpace: "pre-wrap",
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {form.body}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 4, mb: 1.5 }} data-tour="record-lists">
        <Typography component="h2" sx={{ fontWeight: 700, fontSize: "1.1rem", flexGrow: 1 }}>
          Lists
        </Typography>
        {isOwner && (
          <Button
            variant="contained"
            disableElevation
            disabled={!optionsDirty || savingOptions}
            onClick={saveOptions}
            sx={{ textTransform: "none" }}
          >
            Save lists
          </Button>
        )}
      </Box>
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, p: 2, display: "grid", gap: 2.5 }}>
        {!options ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <OptionList
              label="Allergies"
              hint={`Offered when filling in a ${LABELS.client.toLowerCase()}'s ${LABELS.medicalInfo.toLowerCase()}. Staff can still type others.`}
              value={options.allergies}
              disabled={!isOwner}
              onChange={(allergies) => {
                setOptions({ ...options, allergies });
                setOptionsDirty(true);
              }}
            />
            <OptionList
              label="Conditions"
              hint="Chronic conditions, pregnancy, injuries: whatever your team records."
              value={options.conditions}
              disabled={!isOwner}
              onChange={(conditions) => {
                setOptions({ ...options, conditions });
                setOptionsDirty(true);
              }}
            />
            <OptionList
              label="File types"
              hint="Offered when adding a file. A new type typed while uploading is added here too."
              value={options.fileTypes ?? []}
              disabled={!isOwner}
              onChange={(fileTypes) => {
                setOptions({ ...options, fileTypes });
                setOptionsDirty(true);
              }}
            />
          </>
        )}
      </Paper>

      <Dialog open={Boolean(draft)} onClose={() => !saving && setDraft(null)} fullScreen={isMobile} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700 }}>{draft?.id ? "Edit consent form" : "New consent form"}</DialogTitle>
        {draft && (
          <DialogContent sx={{ display: "grid", gap: 2, pt: "8px !important" }}>
            {draft.id && (
              <Alert severity="info">Changes apply to new signatures. Copies already signed keep the old text.</Alert>
            )}
            <TextField
              label="Title"
              size="small"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              inputProps={{ maxLength: 150 }}
            />
            <TextField
              label="Text"
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              multiline
              minRows={8}
              inputProps={{ maxLength: 20000 }}
              placeholder={`I, the undersigned, agree to...`}
            />
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={draft.requiresSignature}
                    onChange={(e) => setDraft({ ...draft, requiresSignature: e.target.checked })}
                  />
                }
                label="Needs a drawn signature"
              />
              <FormControlLabel
                control={<Switch checked={draft.isActive} onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })} />}
                label="In use"
              />
            </Box>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDraft(null)} disabled={saving} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={save}
            disabled={saving || !draft?.title.trim() || !draft?.body.trim()}
            sx={{ textTransform: "none" }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsentForms;
